import IMAGES from "@/public/assets/img";

const credlixremit = {
  seoData: {
    title:
      "CredlixRemit – Direct INR Export Settlement & eFIRA Support | Credlix",
    description:
      "Convert USD, EUR, and GBP export receivables directly into INR within 24 hours with CredlixRemit by Credlix. Get market-competitive rates, lower transfer charges, and hassle-free eFIRA for EDPMS reconciliation.",
  },

  logoTagline: "INR Settlement <br /> For Exporters",
  badgeText: "INR Settlement For Exporters",
  bannerHeading: "You priced the deal in dollars",
  bannerSubHeading: "Keep more of it in rupees",
  formTitle: "What is your last mile costing you?",
  formSubtitle: "Tell us about your next export invoice. Our team will call you back.",
  formData: {
    formType: "inrxpress",
    labels: {
      name: "Your Name*",
      receivables: "Monthly export receivables*",
      contact: "Contact*",
      email: "Work Email*",
      company: "Company Name*",
      agree: "I agree that Credlix may contact me about export finance and settlement solutions.",
    },
    placeholders: {
      name: "Enter Your Name",
      contact: "Enter Your Number",
      email: "Enter Your Email",
      company: "Enter Your Company",
    },
    receivablesOptions: [
      { label: "< $50K", value: "< $50K" },
      { label: "$50K - $100K", value: "$50K - $100K" },
      { label: "$100K - $500K", value: "$100K - $500K" },
      { label: "> $500K", value: "> $500K" },
    ],
    validationMessages: {
      nameRequired: "Name is required.",
      receivablesRequired: "Receivables selection is required.",
      contactRequired: "Phone number is required.",
      contactInvalid: "Please enter a valid 10-digit phone number.",
      emailRequired: "Email is required.",
      emailInvalid: "Please enter a valid email address.",
      companyRequired: "Company name is required.",
      agreeRequired: "You must agree to continue.",
    },
    buttonText: "Activate CredlixRemit",
    footnote: "No obligation. Your details are used only to prepare your savings estimate.",
    successMessage: "Thank you! Your details have been submitted successfully.",
  },

  howItWorks: {
    heading: "Same receivable. ",
    boldHeading: "A cleaner route home.",
    description:
      "Nothing changes about your export factoring facility or your buyer relationship. Only the settlement leg moves.",
    steps: [
      {
        id: 1,
        stepNumber: "1",
        title: "Nominate the invoice",
        description:
          "Flag the USD, EUR or GBP invoices financed under your Credlix facility.",
      },
      {
        id: 2,
        stepNumber: "2",
        title: "Credlix converts",
        description:
          "Foreign currency is converted to INR through Credlix's bankers at market-competitive rates.",
      },
      {
        id: 3,
        stepNumber: "3",
        title: "INR lands in your account",
        description:
          "The rupee equivalent is credited to the CC, OD or Current Account you already operate from.",
      },
      {
        id: 4,
        stepNumber: "4",
        title: "eFIRA is issued",
        description:
          "You get eFIRA for EDPMS reconciliation with your AD bank — no chasing.",
      },
    ],
  },

  whyExportersMove: {
    heading: "More rupees, sooner, ",
    boldHeading: "with the paperwork already done.",
    cards: [
      {
        id: 1,
        title: "Less lost in conversion",
        description:
          "The avoidable spread and per-transaction charges on the settlement leg come down. What you negotiated is closer to what you bank.",
        bgColor: "bg-[#F0FFF0]",
      },
      {
        id: 2,
        title: "No correspondent detour",
        description:
          "Credlixremit directly, so the intermediary deductions between your buyer's bank and yours don't apply on this route.",
        bgColor: "bg-[#E2E9FF]",
      },
      {
        id: 3,
        title: "Usable money in 24 hours",
        description:
          "INR is credited into the working-capital account you already run, within 24 hours, subject to eligibility and cut-off times.",
        bgColor: "bg-[#FEF4EF]",
      },
      {
        id: 4,
        title: "EDPMS stays clean",
        description:
          "eFIRA is shared for reconciliation with your AD bank, cutting the documentation follow-ups your finance team runs every month.",
        bgColor: "bg-[#E2E9FF]",
      },
      {
        id: 5,
        title: "Built for export receivables",
        description:
          "Designed around financed export invoices and their compliance trail — not a general-purpose money transfer app.",
        bgColor: "bg-[#FEF4EF]",
      },
      {
        id: 6,
        title: "A regulated counterparty",
        description:
          "Credlix is an RBI-registered NBFC, IFSCA-registered in GIFT City, and part of the Moglix group.",
        bgColor: "bg-[#F0FFF0]",
      },
    ],
  },

  marginLeakage: {
    heading: "Funding is the easy half.",
    highlightHeading: "The last mile is where margin leaks.",
    subHeading:
      "Five separate charges sit on the settlement leg of every export invoice. Here is what each one takes — and what happens to it on the CredlixRemit route.",
    adBankRoute: {
      title: "Your current AD-bank route",
      topCard: {
        tag: "TYPICAL DEDUCTION - USD 100,000 INVOICE",
        stat: "Up to 2.1%",
        description:
          "Comes off the top before the rupees ever reach your working-capital account — most of it on the conversion, the rest in flat charges nobody itemises for you.",
        legend: [
          { label: "FX spread", color: "#4A9447", width: "40%" },
          { label: "Correspondent", color: "#FECC2F", width: "25%" },
          { label: "SWIFT", color: "#F5681D", width: "18%" },
          { label: "Processing + GST", color: "#D7D7DC", width: "17%" },
        ],
      },
      cards: [
        {
          id: 1,
          tag: "THE LARGEST SINGLE LEAK",
          value: "0.75% – 1.75%",
          description:
            "FX spread over the interbank rate. On a $1M month, each 0.25% you don't negotiate is roughly ₹2.2 lakh — quietly, every month.",
        },
        {
          id: 2,
          tag: "DEDUCTED MID-ROUTE",
          value: "$25 – 40",
          description:
            "Correspondent bank charges, taken before the money reaches your bank.",
        },
        {
          id: 3,
          tag: "PER REMITTANCE",
          value: "$15 – 25",
          description:
            "SWIFT charges, applied whether the transfer is $10,000 or $500,000.",
        },
        {
          id: 4,
          tag: "PLUS 18% GST",
          value: "₹1,000 – 2,000",
          description:
            "Processing charges on every settlement, on top of everything above.",
        },
        {
          id: 5,
          tag: "WORKING CAPITAL, IDLE",
          value: "T+3 working days",
          hasTimeline: true,
          description:
            "Three days between the receivable settling and the rupees being usable.",
        },
        {
          id: 6,
          tag: "COSTS YOUR TEAM, NOT YOUR MARGIN",
          value: "EDPMS follow-ups",
          tags: [
            "Chasing FIRA",
            "Matching shipping bills",
            "Month-end reconciliation",
            "Auditor queries",
          ],
        },
      ],
    },
    inrExpressRoute: {
      title: "With CredlixRemit",
      badge: "CREDLIX • CREDLIXREMIT",
      heading: "Four of those five charges simply don't apply.",
      features: [
        {
          id: 1,
          title: "Minimal SWIFT charges",
          description:
            "Credlixremit domestically in INR, so there is no wire to pay for.",
        },
        {
          id: 2,
          title: "No intermediary bank fees",
          description:
            "No correspondent sits between your buyer's bank and yours on this route.",
        },
        {
          id: 3,
          title: "Nil processing charges",
          description:
            "No per-settlement processing fee — and no GST on a fee that isn't levied.",
        },
        {
          id: 4,
          title: "No GST charges",
          description:
            "The one charge that remains, converted through Credlix's bankers — not a spread you never see.",
        },
      ],
      ctaSubtext:
        "Run your own invoice value and bank spread. Takes 20 seconds.",
    },
    footnote:
      "Illustrative benchmarks, not a guarantee of savings. Actual spreads, charges and settlement timing vary by bank, tenor, currency, ticket size and transaction date, and are subject to eligibility and cut-off times.",
  },

  compareOptions: {
    heading: "Where the leakage goes — ",
    boldHeading: "and where it stops.",
    description:
      "On a typical USD export invoice financed under a Credlix facility.",
    headers: {
      col1: "Cost component",
      col2: "Existing AD-bank route",
      col3: "CredlixRemit by Credlix",
    },
    rows: [
      {
        id: 1,
        component: "SWIFT charges",
        existingRoute: "USD 15 – 25",
        inrExpress: "USD 10",
      },
      {
        id: 2,
        component: "Correspondent bank charges",
        existingRoute: "USD 25 – 40",
        inrExpress: "Remitted directly by Credlix",
      },
      {
        id: 3,
        component: "Processing charges",
        existingRoute: "₹1,000 – 2,000 + 18% GST",
        inrExpress: "Reduced avoidable charges",
      },
      {
        id: 4,
        component: "Settlement time",
        existingRoute: "T + 3 working days",
        inrExpress: "Within 24 hours*",
      },
      {
        id: 5,
        component: "EDPMS documentation",
        existingRoute: "Coordinated with AD bank",
        inrExpress: "eFIRA shared directly",
      },
    ],
    footnote:
      "*Illustrative benchmarks, not a guarantee of savings. Exact spreads, charges and settlement timing vary by bank, tenor, currency and ticket size, and are subject to eligibility and cut-off times.",
  },
  ConuterSec: {
    heading: "Trade finance Indian exporters already run on",
    paragraph: "The CREDLIX TRACK RECORD",
    bannerImg: IMAGES.HomeNew.numbersBg,
    bannerMbImage: IMAGES.HomeNew.numbersBgMobile,
    counterList: [
      { heading: "75,000", paragraph: "Invoice Funded", smalltext: "+" },
      { heading: "5000", paragraph: "MSMEs Onboarded", smalltext: "+" },
      {heading:'$1', paragraph:'Export Financing', smalltext:'Bn+'},
      { heading: "120", paragraph: "Cities Served", smalltext: "+" },
    ],
  },

  FaqHead: "Questions exporters ask first",
  Faq: [
    {
      id: 0,
      question: "What is CredlixRemit by Credlix?",
      answers:
        "CredlixRemit is Credlix's INR-settlement proposition for eligible export-factoring clients. It helps convert financed USD/EUR/GBP export receivables into INR and credit the equivalent amount directly to your CC / OD / Current Account, with eFIRA support for EDPMS.",
    },
    {
      id: 1,
      question: "Who can use it?",
      answers:
        "At launch, it is designed for existing Credlix export-factoring clients. Final eligibility is confirmed by Credlix Product and Compliance.",
    },
    {
      id: 2,
      question: "Which currencies are supported?",
      answers:
        "USD, EUR and GBP at launch. Additional currencies will be added as the product expands.",
    },
    {
      id: 3,
      question: "Does it replace export factoring?",
      answers:
        "No — it complements it. The underlying export receivable stays the same; only the settlement currency and route change.",
    },
    {
      id: 4,
      question: "Will I receive eFIRA?",
      answers:
        "Yes — eFIRA is shared for EDPMS reconciliation with your AD bank. The exact issuing entity is confirmed during onboarding.",
    },
    {
      id: 5,
      question: "How fast is settlement?",
      answers:
        "Faster than a typical AD-bank route — within 24 hours, subject to eligibility and cut-off times.",
    },
    {
      id: 6,
      question: "Is there any fee?",
      answers:
        "You benefit from market-competitive conversion and reduced avoidable charges on the Credlix route. Exact terms are shared before you transact.",
    },
    {
      id: 7,
      question: "What account can receive the INR?",
      answers:
        "CC, OD or Current Account. Your RM confirms account eligibility during onboarding.",
    },
    {
      id: 8,
      question: "How do I start?",
      answers:
        "Confirm your account details with your Credlix RM, nominate eligible invoices, and receive INR with eFIRA support.",
    },
  ],
};

export default credlixremit;
